import { AxiosResponse } from "axios";
import { ApiResponse, ErrorResponse } from "middleware/api";
import { actions } from "reducers/slotCreation";
import { actions as notificationActions } from "../reducers/notification";
import { actions as slotsActions } from "../reducers/slots";
import { actions as sessionActions } from "../reducers/session";
import { store } from "../store";
import * as regionsMiddleware from "middleware/regions";
import * as slotsMiddleware from "middleware/slots";
import {
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from "redux-saga/effects";
import { WorkzonesResponseType } from "./types/regions";
import { PayloadAction } from "@reduxjs/toolkit";
import { SlotCreationBodyType } from "reducers/types/slotCreation";
import { SlotCreationResponseType } from "./types/slotCreation";
import { DATE_FORMATS } from "utils/constants";
import moment from "moment";
import i18next from "config/i18next";
import { SlotCreationValues } from "pages/slots/slotsDetail/slotCreationModal/types";
import { SlotsConstants } from "constants/slots";

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getWorkzonesFetch.type, getWorkzones),
  takeLatest(actions.createSlotRequest.type, createSlot),
];

export default sagas;

function* getWorkzones({
  payload,
}: PayloadAction<string>): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<WorkzonesResponseType>>>,
  void,
  ApiResponse<WorkzonesResponseType>
> {
  const response = yield call(regionsMiddleware.getWorkzonesByRegion, payload);
  if (response.status !== 200) {
    yield put(actions.getWorkzonesError());
  } else {
    const { result } = response.data;
    yield put(actions.getWorkzonesSuccess(result.items));
  }
}

function* createSlot({
  payload,
}: PayloadAction<SlotCreationValues>): Generator<
  | CallEffect<AxiosResponse<SlotCreationResponseType & ErrorResponse>>
  | PutEffect<{ type: string }>
  | void,
  void,
  AxiosResponse<SlotCreationResponseType & ErrorResponse>
> {
  const paramsPost: SlotCreationBodyType = {
    regionId: payload.region.id,
    workZoneId: payload.workzone.id,
    startAt: `${payload.slotTime.initial}:00`,
    finishAt: `${payload.slotTime.end}:00`,
    date: moment(payload.date, DATE_FORMATS.shortDate).format(
      DATE_FORMATS.shortISODate
    ),
    capacity: payload.capacity,
  };

  const { status, data } = yield call(slotsMiddleware.createSlot, paramsPost);
  if (status !== 200 && status !== 201) {
    yield put(actions.createSlotError());
    if (data.statusCode === SlotsConstants.DATE_TIME_SLOT_NOT_ALLOWED_ERROR) {
      yield put(
        notificationActions.showNotification({
          id: "notification-slot-creation-error",
          level: "error",
          title: i18next.t("slotCreation:createSlotErrorTitle"),
          body: i18next.t("slotCreation:createSlotErrorMessage"),
          onClose: payload.refresh,
        })
      );
    } else {
      yield put(
        notificationActions.showNotification({
          id: "notification-slot-creation-error",
          level: "error",
          title: i18next.t("slotCreation:invalidTitle"),
          body: i18next.t("slotCreation:invalidMessage"),
        })
      );
    }
  } else {
    yield put(actions.createSlotSuccess());
    const { filters, filtersExtra } = store.getState().slots;
    yield put(
      notificationActions.showNotification({
        id: "notification-slot-creation-success",
        level: "success",
        title: i18next.t("slotCreation:validTitle"),
        body: i18next.t("slotCreation:validMessage", {
          data: data.result.id,
        }),
        onClose: () => {
          store.dispatch(slotsActions.toggleCreationModalVisibility(false));
          store.dispatch(
            slotsActions.getSlotsRequest({
              ...filters,
              ...filtersExtra,
            })
          );
          store.dispatch(sessionActions.setDirty(false));
        },
      })
    );
  }
}
